import * as React from 'react';
import * as _ from 'lodash';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ITransformHeader, ITransformRow, TransformFormatType } from '../../../../models/ITransform';
import { TransformCell } from './TransformCell';
import cancelEvent from '../../../../services/mini/cancelEvent';
import { getTransformRowWithoutId } from './getTransformText';
import Overlay from '../../../components/Overlay/Overlay';
import { createPortal } from 'react-dom';
import ConfirmPopup from '../../../components/ConfirmPopup/ConfirmPopup';
import { confirmAlert } from '../../../../services/mini/alanConfirmAlert';
import TransformRowPopup from './TransformRowPopup/TransformRowPopup';
import { RectButton } from '../../../components/buttons/RectButton/RectButton';
import RunScriptButton from './RunScriptButton/RunScriptButton';
import { Environment } from '../../../../const/environment';
import { transformsStore } from '../../../../mobx/transforms.store';
import { observer } from 'mobx-react';
import { getTransformRowCells } from './getTransformRowCells';

type TransformRowProps = {
    projectId: string,
    transformName: string,
    header: ITransformHeader,
    row: ITransformRow,
    savedRow: ITransformRow,
    onCopy?: (row: ITransformRow) => void,
    onDuplicate: (row: ITransformRow) => void,
    onCopyToBuffer: (row: ITransformRow) => void,
    onFormatUpdate: (name: string, val: TransformFormatType) => void,
    environment: Environment
    index,
    previewMode: boolean
    isHighlighted?: boolean
    readOnly: boolean
};

function TransformRowView({ projectId, readOnly, row, savedRow, header, environment, onCopy, onDuplicate, onCopyToBuffer, onFormatUpdate, index, previewMode, transformName, isHighlighted }: TransformRowProps) {

    const [editMode, setEditMode] = useState<boolean>(!previewMode);
    const [popupOpen, setPopupOpen] = useState<string | false | null>(false);
    const [animated, setAnimated] = useState<boolean>(false);

    // console.info('=== Transform Row', index);

    useEffect(() => {
        if (isHighlighted) {
            setAnimated(true);
        }
    }, [isHighlighted]);

    let hasChanges = false;
    const changesInRow = {
        input: false,
        query: false,
        prompt: false,
        result: false,
    };

    try {
        const rowWithoutId = getTransformRowWithoutId(row, true);
        const savedRowWithoutId = getTransformRowWithoutId(savedRow, true);

        hasChanges = JSON.stringify(rowWithoutId) !== (savedRow ? JSON.stringify(savedRowWithoutId) : '');
        changesInRow.input = (row.input ? JSON.stringify(rowWithoutId.input) : '') !== (savedRow.input ? JSON.stringify(savedRowWithoutId.input) : '');
        changesInRow.query = (row.query ? JSON.stringify(rowWithoutId.query) : '') !== (savedRow.query ? JSON.stringify(savedRowWithoutId.query) : '');
        changesInRow.prompt = (row.prompt ? JSON.stringify(rowWithoutId.prompt) : '') !== (savedRow.prompt ? JSON.stringify(savedRowWithoutId.prompt) : '');
        changesInRow.result = (row.result ? JSON.stringify(rowWithoutId.result) : '') !== (savedRow.result ? JSON.stringify(savedRowWithoutId.result) : '');
    } catch (error) {

    }

    if (!savedRow && !previewMode) {
        if (index === 0) {
            changesInRow.input = !_.isEmpty(row.input);
            changesInRow.query = !_.isEmpty(row.query);
            changesInRow.prompt = !_.isEmpty(row.prompt);
            changesInRow.result = !_.isEmpty(row.result);
        } else {
            changesInRow.input = true;
            changesInRow.query = true;
            changesInRow.prompt = true;
            changesInRow.result = true;
        }
    }

    const onUpdate = useCallback((prop, value) => {
        transformsStore.updateRow(prop, value, row.id);
    }, [row?.id]);

    const ref = React.useRef<HTMLTableRowElement>();

    const clearRow = useCallback(() => {
        transformsStore.markRowToDelete(row.id);
    }, [row.id]);

    // console.info('=== changesInRow', changesInRow);

    const isEmptyRow = (row: ITransformRow) => {
        return (
            (!row.input || (row.input?.trim && row.input?.trim() === "")) &&
            (!row.query || (row.query?.trim && row.query?.trim() === "")) &&
            (!row.prompt || (row.prompt?.trim && row.prompt?.trim() === "")) &&
            (!row.result || (row.result?.trim && row.result?.trim() === ""))
        );
    };

    const isEmptyRowResult = (row: ITransformRow) => {
        return (!row.result || (row.result?.trim && row.result?.trim() === ""));
    };

    const onEnterEditMode = () => {
        setEditMode(true);
    };

    const onClosePopup = () => {
        setPopupOpen(false);
    };

    const onOpenPopup = (name) => {
        setPopupOpen(name);
    };

    const forDelete = row.deleting;

    const cells = useMemo(() => {
        return getTransformRowCells(previewMode);
    }, [previewMode]);

    return <div className={`transform-view__table-row ${animated ? 'highlighted' : ''} ${forDelete && 'will-be-removed'}`} ref={ref}>
        <div className="transform-view__row-number-cell">
            {index + 1}
        </div>

        {cells.map(cell => {
            return <TransformCell
                key={cell.name}
                row={row}
                hasChanges={changesInRow[cell.name]}
                transformName={transformName}
                editable={editMode && !readOnly}
                previewMode={previewMode}
                name={cell.name}
                placeholder={cell.placeholder}
                onEnterEditMode={onEnterEditMode}
                onUpdate={onUpdate}
                onFormatUpdate={onFormatUpdate}
                onClosePopup={onClosePopup}
                onOpenPopup={onOpenPopup}
                header={header}
                index={index}
                environment={environment}
                isPopupShown={popupOpen !== false}
            />
        })}

        <div className="transform-view__command-cell ">
            <div className="transform-view__command-cell-content">
                <RectButton
                    title="Open row preview"
                    className={forDelete ? 'disabled' : ''}
                    onClick={(e) => {
                        cancelEvent(e);
                        onOpenPopup(null);
                    }}
                    children={<i className="icon icon-alan-search" />}
                />

                <RunScriptButton
                    text={row.result || ''}
                    className={`${(forDelete || header.result.format !== 'javascript' || isEmptyRowResult(row)) ? 'disabled' : ''}`}
                    transformName={transformName}
                    environment={environment} />

                {editMode ? <>
                    <RectButton
                        title="Discard local changes for this row"
                        className={`${hasChanges ? '' : 'disabled'} ${readOnly ? 'disabled' : ''}`}
                        onClick={(e) => {
                            confirmAlert({
                                customUI: ({ onClose }) => {
                                    return (
                                        <ConfirmPopup
                                            onClose={onClose}
                                            okBtnLabel="Discard"
                                            okBtnCb={() => {
                                                cancelEvent(e);
                                                transformsStore.discardRowChange(row.id, savedRow);
                                                onClose();
                                            }}
                                            title="Discard changes"
                                            message={<div>Are you sure you want to discard changes for this row?</div>}
                                        />
                                    );
                                }
                            });

                        }}
                        children={<i className="icon icon-alan-arrow-round-right-left-fat" />}
                    />

                    <RectButton
                        title="Duplicate this row and add to the end of the table"
                        className={(readOnly || forDelete) ? 'disabled' : ''}
                        onClick={(e) => {
                            cancelEvent(e);
                            onDuplicate(row);
                        }}
                        children={<i className="icon icon-alan-stat-sort-down" />}
                    />
                </> :
                    <>
                        <RectButton
                            title="Add to transform example"
                            onClick={() => {
                                if (readOnly) {
                                    confirmAlert({
                                        customUI: ({ onClose }) => {
                                            return (
                                                <ConfirmPopup
                                                    onClose={onClose}
                                                    cancelBtnLabel="Cancel"
                                                    okBtnLabel="Continue"
                                                    okBtnCb={() => {
                                                        if (onCopy) {
                                                            onCopy(row);
                                                        }
                                                        onClose();
                                                    }}
                                                    title="Info"
                                                    message={<div>Row copying requires a switch to the Development environment to the Last version. Are you sure you want to continue?</div>}
                                                />
                                            );
                                        }
                                    });
                                } else {
                                    if (onCopy) {
                                        onCopy(row);
                                    }
                                }
                            }}
                            children={<i className="icon icon-alan-plus-sign" />}
                        />
                    </>}

                <RectButton
                    title="Copy row to buffer"
                    className={forDelete ? 'disabled' : ''}
                    onClick={(e) => {
                        cancelEvent(e);
                        onCopyToBuffer(row);
                    }}
                    children={<i className="icon icon-alan-test-copy" />}
                />

                {editMode &&
                    <RectButton
                        title="Delete row"
                        className={(readOnly || isEmptyRow(row)) ? 'disabled' : ''}
                        onClick={clearRow}
                        children={<i className="icon icon-alan-trash" />}
                    />}

            </div>
            {popupOpen !== false && createPortal(
                <Overlay>
                    <TransformRowPopup
                        projectId={projectId}
                        row={row}
                        index={index}
                        transformName={transformName}
                        openedBy={popupOpen}
                        environment={environment}
                        editable={editMode && !readOnly}
                        previewMode={previewMode}
                        header={header}
                        onClose={onClosePopup} />
                </Overlay>,
                document.body
            )}
        </div>
    </div>;
}

export const TransformRow = observer(TransformRowView);